import { ResponseRateApi } from 'api/ResponseRateApi';
import { TrendApi } from 'api/TrendApi';
import { CompareGroupApi } from 'api/CompareGroupApi';
import { ProjectStatus } from 'model/ProjectStatus';
import { BenchmarkApi } from 'api/BenchmarkApi';
import { ScoreApi } from 'api/ScoreApi';
import { AxiosInstance, AxiosRequestConfig } from 'axios';
import {
  mapSummaryPulseComparedDtoToModel,
  SummaryPulseComparedDto,
  SummaryPulseComparedModel,
} from 'api/dto/new/SummaryPulseCompared.dto';
import {
  mapSummaryPulseOnDemandComparedDtoToSimpleProjectCompare,
  SummaryPulseOnDemandComparedDto,
} from 'api/dto/new/SummaryPulseOnDemandCompared.dto';
import { SimpleProject } from 'model/SimpleProject';
import {
  mapSummaryPulseOnDemandCombinedDtoToSimpleProjectCombine,
  SummaryPulseOnDemandCombinedDto,
} from 'api/dto/new/SummaryPulseOnDemandCombined.dto';
import {
  mapSummaryPulseLifecycleComparedDtoToSimpleProjectCompare,
  SummaryPulseLifecycleComparedDto,
} from 'api/dto/new/SummaryPulseLifecycleCompared.dto';
import {
  mapSummaryPulseLifecycleCombinedDtoToSimpleProjectCombine,
  SummaryPulseLifecycleCombinedDto,
} from 'api/dto/new/SummaryPulseLifecycleCombined.dto';
import {
  mapSummaryPulseCombinedDtoToModel,
  SummaryPulseCombinedDto,
  SummaryPulseCombinedModel,
} from 'api/dto/new/SummaryPulseCombined.dto';

export namespace SummaryApi {
  export type EntryCombine = {
    score: ScoreApi.Entry;
    benchmark: BenchmarkApi.Entry | null;
    trends?: TrendApi.DataEntry[];
    responseRate: ResponseRateApi.Entry;
  };

  export type EntryCompare = {
    label: string;
    byGroup: Record<string, CompareGroupApi.Entry>;
  };

  export type ProjectSummary = {
    id: number;
    benchmark: number;
    date: string;
    score: number | null;
    type: ProjectStatus;
  };
}

export class SummaryApi {
  constructor(private client: AxiosInstance) {}

  summaryPulseCombined(
    { projectId, query }: { projectId: number; query: string },
    { signal }: AxiosRequestConfig = {}
  ): Promise<SummaryPulseCombinedModel | void> {
    return this.client
      .get<SummaryPulseCombinedDto>(
        `/api/survey/${projectId}/report/summary-pulse-combined${query}`,
        { signal }
      )
      .then((response) => response.data)
      .then(mapSummaryPulseCombinedDtoToModel)
      .catch((error) => {
        if (!signal?.aborted) {
          throw error.response ? error.response.data.error : error;
        }
      });
  }

  summaryPulseCompared(
    { projectId, query }: { projectId: number; query: string },
    { signal }: AxiosRequestConfig = {}
  ): Promise<SummaryPulseComparedModel | void> {
    return this.client
      .get<SummaryPulseComparedDto>(
        `/api/survey/${projectId}/report/summary-pulse-compared${query}`,
        { signal }
      )
      .then((response) => response.data)
      .then(mapSummaryPulseComparedDtoToModel)
      .catch((error) => {
        if (!signal?.aborted) {
          throw error.response ? error.response.data.error : error;
        }
      });
  }

  summaryPulseOnDemandCombined(
    { projectId, query }: { projectId: number; query: string },
    { signal }: AxiosRequestConfig = {}
  ): Promise<SimpleProject.Combine | void> {
    return this.client
      .get<SummaryPulseOnDemandCombinedDto>(
        `/api/survey/${projectId}/report/summary-pulse-on-demand-combined${query}`,
        { signal }
      )
      .then((response) => response.data)
      .then(mapSummaryPulseOnDemandCombinedDtoToSimpleProjectCombine)
      .catch((error) => {
        if (!signal?.aborted) {
          throw error.response ? error.response.data.error : error;
        }
      });
  }

  summaryPulseOnDemandCompared(
    { projectId, query }: { projectId: number; query: string },
    { signal }: AxiosRequestConfig = {}
  ): Promise<SimpleProject.Compare | void> {
    return this.client
      .get<SummaryPulseOnDemandComparedDto>(
        `/api/survey/${projectId}/report/summary-pulse-on-demand-compared${query}`,
        { signal }
      )
      .then((response) => response.data)
      .then(mapSummaryPulseOnDemandComparedDtoToSimpleProjectCompare)
      .catch((error) => {
        if (!signal?.aborted) {
          throw error.response ? error.response.data.error : error;
        }
      });
  }

  summaryPulseLifecycleCombined(
    { projectId, query }: { projectId: number; query: string },
    { signal }: AxiosRequestConfig = {}
  ): Promise<SimpleProject.Combine | void> {
    return this.client
      .get<SummaryPulseLifecycleCombinedDto>(
        `/api/survey/${projectId}/report/summary-pulse-lifecycle-combined${query}`,
        { signal }
      )
      .then((response) => response.data)
      .then(mapSummaryPulseLifecycleCombinedDtoToSimpleProjectCombine)
      .catch((error) => {
        if (!signal?.aborted) {
          throw error.response ? error.response.data.error : error;
        }
      });
  }

  summaryPulseLifecycleCompared(
    { projectId, query }: { projectId: number; query: string },
    { signal }: AxiosRequestConfig = {}
  ): Promise<SimpleProject.Compare | void> {
    return this.client
      .get<SummaryPulseLifecycleComparedDto>(
        `/api/survey/${projectId}/report/summary-pulse-lifecycle-compared${query}`,
        { signal }
      )
      .then((response) => response.data)
      .then(mapSummaryPulseLifecycleComparedDtoToSimpleProjectCompare)
      .catch((error) => {
        if (!signal?.aborted) {
          throw error.response ? error.response.data.error : error;
        }
      });
  }
}
